import { useEffect, useState } from "react";
import { fetchPlaceDetails } from "../utils/googlePlaces";

/**
 * Load place details from Google Place Details.
 * Never reads deprecated DB `address` / `openingHours`.
 *
 * @param {string|null|undefined} placeId - Google Place ID
 * @returns {{
 *   address: string|null,
 *   openingHours: Array<{days: string, times: string}>|null,
 *   photoUrls: string[],
 *   phoneNumber: string|null,
 *   websiteUri: string|null,
 *   openNow: boolean|null,
 *   price: string|null,
 *   editorialSummary: string|null,
 *   isLoading: boolean,
 * }}
 */
export function useGooglePlaceDetails(placeId) {
  const [address, setAddress] = useState(null);
  const [openingHours, setOpeningHours] = useState(null);
  const [photoUrls, setPhotoUrls] = useState([]);
  const [phoneNumber, setPhoneNumber] = useState(null);
  const [websiteUri, setWebsiteUri] = useState(null);
  const [openNow, setOpenNow] = useState(null);
  const [price, setPrice] = useState(null);
  const [editorialSummary, setEditorialSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const reset = () => {
      setAddress(null);
      setOpeningHours(null);
      setPhotoUrls([]);
      setPhoneNumber(null);
      setWebsiteUri(null);
      setOpenNow(null);
      setPrice(null);
      setEditorialSummary(null);
      setIsLoading(false);
    };

    if (!placeId || typeof placeId !== "string") {
      reset();
      if (__DEV__) {
        console.log("[useGooglePlaceDetails] skipped: no googlePlaceId on restaurant");
      }
      return undefined;
    }

    // Synthetic House Pick IDs are not real Google Place IDs.
    if (placeId.startsWith("housepick-ai-")) {
      reset();
      return undefined;
    }

    setIsLoading(true);
    fetchPlaceDetails(placeId)
      .then((details) => {
        if (cancelled) return;
        setAddress(details?.address ?? null);
        setOpeningHours(details?.openingHours ?? null);
        setPhotoUrls(Array.isArray(details?.photoUrls) ? details.photoUrls : []);
        setPhoneNumber(details?.phoneNumber ?? null);
        setWebsiteUri(details?.websiteUri ?? null);
        setOpenNow(typeof details?.openNow === "boolean" ? details.openNow : null);
        setPrice(details?.price ?? null);
        setEditorialSummary(details?.editorialSummary ?? null);
        if (__DEV__) {
          console.log("[useGooglePlaceDetails]", {
            placeId,
            address: details?.address ? "yes" : "no",
            photos: details?.photoUrls?.length ?? 0,
            phone: details?.phoneNumber ? "yes" : "no",
            website: details?.websiteUri ? "yes" : "no",
            openNow: details?.openNow,
            price: details?.price || null,
            editorial: details?.editorialSummary ? "yes" : "no",
          });
        }
      })
      .catch(() => {
        if (cancelled) return;
        reset();
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [placeId]);

  return {
    address,
    openingHours,
    photoUrls,
    phoneNumber,
    websiteUri,
    openNow,
    price,
    editorialSummary,
    isLoading,
  };
}
